import { useState, useCallback, useMemo, useRef } from 'react';
import { getTabForHash, TabType } from '../routing/parseHashRoute';

export type { TabType };
export type NavigationDirection = 'forward' | 'back' | 'none';

const TAB_ORDER: Record<TabType, number> = {
  home: 0,
  goals: 1,
  time: 2,
  progress: 3,
};

// Route depth hierarchy for direction detection
function getRouteDepth(hash: string): number {
  const path = (hash.replace('#', '') || '/').split('?')[0];
  if (/^\/subgoals\/[^/]+\/todos\/[^/]+$/.test(path)) return 4; // todo-detail
  if (/^\/subgoals\/[^/]+\/todos$/.test(path)) return 3;        // subgoal-todos
  if (/^\/goals\/[^/]+$/.test(path)) return 2;                  // goal-detail
  if (path === '/profile') return 1;
  // Tab-level pages: /home, /goals, /ongoing, /timely
  return 0;
}

const TODO_HASH = /^#?\/subgoals\/([^/]+)\/todos\/[^/?]+$/;

/** True for a task's address, which opens the drawer over another page (T-183). */
export function isTaskHash(hash: string): boolean {
  return TODO_HASH.test(hash);
}

/**
 * The page under the task drawer: the last page before it, or (for a task opened by its
 * link) the task's stage in Hedefler.
 */
export function underlayFor(hash: string, previous: string | null): string {
  const match = hash.match(TODO_HASH);
  if (!match) return hash;
  return previous ?? `#/subgoals/${match[1]}/todos`;
}

function getRouteKey(hash: string): string {
  const path = (hash.replace('#', '') || '/home').split('?')[0];
  // Hedefler is one page for the list, a goal, a stage and the drafts (T-182): it stays
  // mounted (no page transition) while the selection changes.
  if (path === '/goals/new') return path;
  if (path === '/goals' || path === '/ai-drafts' || /^\/goals\/[^/]+$/.test(path) || /^\/subgoals\/[^/]+\/todos$/.test(path)) {
    return '/goals';
  }
  // Normalize top-level routes
  if (path === '/' || path === '') return '/home';
  return path;
}

interface UseNavigationReturn {
  /** Derived from the current hash; null on pages outside the tab bar. */
  activeTab: TabType | null;
  /** The page under the task drawer while a task is open; else the current hash. */
  underlayHash: string;
  /** True when a page of this visit is under the drawer, so Back closes it. */
  hasUnderlay: boolean;
  navigateToTab: (tab: TabType) => void;
  navigateToGoal: (goalId: string) => void;
  navigateToSubGoalTodos: (subGoalId: string) => void;
  goBack: () => void;
  navigationDirection: NavigationDirection;
  routeKey: string;
  computeDirection: (newHash: string) => NavigationDirection;
  setNavigationDirection: (dir: NavigationDirection) => void;
}

export const useNavigation = (): UseNavigationReturn => {
  const [currentHash, setCurrentHash] = useState(() => window.location.hash);
  // The last page that was not a task: it stays mounted under the drawer
  const lastPageRef = useRef<string | null>(isTaskHash(window.location.hash) ? null : window.location.hash);
  const [hasUnderlay, setHasUnderlay] = useState(false);
  const underlayHash = useMemo(() => underlayFor(currentHash, lastPageRef.current), [currentHash]);
  const activeTab = useMemo(() => getTabForHash(underlayHash), [underlayHash]);
  const [navigationDirection, setNavigationDirection] = useState<NavigationDirection>('none');
  const [routeKey, setRouteKey] = useState(() => getRouteKey(underlayFor(window.location.hash, null)));
  const prevHashRef = useRef(window.location.hash);
  const routeHistoryRef = useRef<string[]>([window.location.hash]);
  const isGoingBackRef = useRef(false);

  const computeDirection = useCallback((newHash: string): NavigationDirection => {
    const prev = prevHashRef.current;
    if (prev === newHash) return 'none';

    // Opening, walking or closing the drawer keeps the page under it as it is
    if (isTaskHash(newHash) || isTaskHash(prev)) {
      const opened = isTaskHash(newHash);
      if (!opened) lastPageRef.current = newHash;
      setHasUnderlay(opened && lastPageRef.current !== null);
      prevHashRef.current = newHash;
      setCurrentHash(newHash);
      setRouteKey(getRouteKey(underlayFor(newHash, lastPageRef.current)));
      return 'none';
    }
    lastPageRef.current = newHash;

    let direction: NavigationDirection;

    // If goBack was explicitly called
    if (isGoingBackRef.current) {
      isGoingBackRef.current = false;
      direction = 'back';
    } else {
      const prevDepth = getRouteDepth(prev);
      const newDepth = getRouteDepth(newHash);

      // Deeper route = forward, shallower = back
      if (newDepth > prevDepth) {
        direction = 'forward';
      } else if (newDepth < prevDepth) {
        direction = 'back';
      } else {
        // Same depth — tab-to-tab transition: use tab order
        const prevTab = getTabForHash(prev) as TabType;
        const newTab = getTabForHash(newHash) as TabType;
        if (TAB_ORDER[prevTab] !== undefined && TAB_ORDER[newTab] !== undefined) {
          direction = TAB_ORDER[newTab] > TAB_ORDER[prevTab] ? 'forward' : 'back';
        } else {
          direction = 'forward';
        }
      }
    }

    // Update route history and key
    if (direction === 'back') {
      if (routeHistoryRef.current.length > 1) routeHistoryRef.current.pop();
    } else {
      routeHistoryRef.current.push(newHash);
    }
    prevHashRef.current = newHash;
    setCurrentHash(newHash);
    setRouteKey(getRouteKey(newHash));

    return direction;
  }, []);

  const navigateToTab = useCallback((tab: TabType) => {
    const hashMap: Record<TabType, string> = {
      home: '#/home',
      goals: '#/goals',
      time: '#/time',
      progress: '#/progress',
    };
    window.location.hash = hashMap[tab] || '#/home';
  }, []);

  const navigateToGoal = useCallback((goalId: string) => {
    window.location.hash = `#/goals/${goalId}`;
  }, []);

  const navigateToSubGoalTodos = useCallback((subGoalId: string) => {
    window.location.hash = `#/subgoals/${subGoalId}/todos`;
  }, []);

  const goBack = useCallback(() => {
    isGoingBackRef.current = true;
    const history = routeHistoryRef.current;

    if (history.length > 1) {
      // Navigate to previous route in our stack
      const parentHash = history[history.length - 2];
      window.location.hash = parentHash;
    } else {
      // Fallback: smart parent detection from current hash
      const currentPath = window.location.hash.replace('#', '') || '/';
      let parentHash = '#/home';

      if (/^\/subgoals\/[^/]+\/todos\/[^/]+$/.test(currentPath)) {
        // todo-detail → subgoal-todos
        const subgoalId = currentPath.match(/^\/subgoals\/([^/]+)\/todos/)?.[1];
        parentHash = subgoalId ? `#/subgoals/${subgoalId}/todos` : '#/goals';
      } else if (/^\/subgoals\/[^/]+\/todos$/.test(currentPath)) {
        // subgoal-todos → goals (we don't know the goal ID easily)
        parentHash = '#/goals';
      } else if (/^\/goals\/[^/]+$/.test(currentPath)) {
        // goal-detail → goals
        parentHash = '#/goals';
      } else if (currentPath === '/profile') {
        parentHash = '#/home';
      }

      window.location.hash = parentHash;
    }
  }, []);

  return {
    activeTab,
    underlayHash,
    hasUnderlay,
    navigateToTab,
    navigateToGoal,
    navigateToSubGoalTodos,
    goBack,
    navigationDirection,
    routeKey,
    computeDirection,
    setNavigationDirection,
  };
};
